import { create } from 'zustand';
import { CALL_OUTCOMES_INTERESTED } from '@claimup/shared/constants/calls';

/**
 * La sesión de llamadas del gestor.
 *
 * Vive en un store global y **no** en el estado de la página por un motivo
 * concreto: la pata de audio WebRTC se abre UNA vez al activar sesión y no se
 * cierra hasta que el gestor la cierra. Si el cliente colgara de un componente,
 * cualquier navegación lo desmontaría y cortaría el audio a media conversación.
 *
 * Y hay una razón de producto además de la técnica: mantener la conexión abierta
 * toda la sesión es lo que hace que no haya "conectando…", ni pitido de
 * handshake, ni medio segundo de retraso cuando entra una llamada. El deudor dice
 * "¿Sí?" y el gestor ya lo está oyendo.
 *
 * ⚠️ El cliente de Telnyx se guarda aquí **fuera de React**: es un objeto con
 * conexión viva, no estado serializable. No se pone en `localStorage` ni se
 * compara por igualdad.
 */

/**
 * Los estados por los que pasa un gestor. Los mismos nombres que usa el backend
 * para decidir si tiene hueco, a propósito: dos vocabularios para lo mismo
 * acaban en un bug donde el front cree que está libre y el back que no.
 */
export const SESSION_STATUS = {
  OFFLINE: 'offline',
  CONNECTING: 'connecting',
  READY: 'ready',
  DIALING: 'dialing',
  CONNECTED: 'connected',
  WRAP: 'wrap',
  PAUSED: 'paused',
};

const INITIAL = {
  status: SESSION_STATUS.OFFLINE,
  // El cliente WebRTC y la llamada activa. Fuera del ciclo de render.
  client: null,
  /**
   * La pata de audio del gestor.
   *
   * ⚠️ **Es de la SESIÓN, no de la llamada.** Desde que la pata se aparca entre
   * llamadas, esto se abre una vez al empezar la jornada y no se cierra hasta
   * que el gestor cierra sesión. Por eso `startDialing` ya no lo vacía: si lo
   * hiciera, de la segunda llamada en adelante el front creería que no hay audio.
   */
  activeCall: null,
  /**
   * ¿ESTA llamada está puenteada?
   *
   * Existe porque `activeCall` dejó de servir para saberlo. Antes bastaba —la
   * pata nacía y moría con la llamada—, y ahora diría que sí siempre, incluso
   * mientras suena el teléfono de la siguiente. De ahí sale `hadAudio`, que es lo
   * que separa una conversación de un buzón en los KPIs del gestor.
   */
  conectado: false,
  // A quién se está llamando AHORA: es lo que se pinta durante los tonos, y por
  // eso se rellena al empezar a marcar y no al descolgar. La espera es el
  // briefing.
  current: null,
  // La marcación anterior, mientras se tipifica. Convive con `current` a
  // propósito: el gestor cierra la anterior mientras suena la siguiente, que es
  // lo que recupera los ~20 s de wrap.
  pendingOutcome: null,
  /**
   * Lo que se ofrece al gestor justo después de tipificar: ir al expediente,
   * crear la tarea acordada o cancelar la llamada que el buzón ha programado.
   *
   * ⚠️ No bloquea nada. La siguiente llamada puede entrar con esto abierto y la
   * jornada no se para: si cerrarlo fuera obligatorio, cada tipificación
   * costaría los segundos que este rediseño pretende ahorrar.
   */
  accionesTrasTipificar: null,
  /**
   * Aviso de audio SALIENTE del SDK, si lo hay.
   *
   * Solo los de nuestra pata: que el deudor esté callado no es un problema que
   * el gestor pueda arreglar, y avisar de eso sería el cuento del lobo. Que
   * nuestro micrófono esté mudo sí lo es, y hasta ahora el SDK lo gritaba cada
   * seis segundos en una consola que nadie mira.
   */
  mediaWarning: null,

  /** Filtro de llamadas detectado: hay una persona detrás de la locución. */
  screening: false,

  queueDepth: null,
  stats: { conversations: 0, promises: 0, committedAmount: 0 },
  error: null,
};

export const useCallSessionStore = create((set, get) => ({
  ...INITIAL,

  setStatus: (status) => set({ status }),
  setQueueDepth: (queueDepth) => set({ queueDepth }),
  setMediaWarning: (mediaWarning) => set({ mediaWarning }),

  /**
   * Al otro lado hay un FILTRO de llamadas, no un contestador.
   *
   * Suenan igual —una locución— y por eso hay que decirlo: la diferencia es que
   * aquí hay una persona esperando a que el gestor diga su nombre, y si cuelga
   * creyendo que es un buzón pierde a alguien localizable. Uno de cada cinco
   * `machine` resultó ser esto al escuchar las grabaciones del 24/09.
   */
  setScreening: (screening) => set({ screening }),
  setError: (error) => set({ error }),

  /** Guarda el cliente WebRTC ya conectado. */
  attachClient: (client) => set({ client }),

  /**
   * Empieza una marcación. La ficha aparece AHORA, mientras suena: los ~22 s de
   * tono son el briefing del gestor, no tiempo muerto.
   */
  startDialing: (target) =>
    // El aviso se limpia en cada marcación nueva: si no, un micrófono que falló
    // hace media hora seguiría en rojo toda la jornada.
    set({
      status: SESSION_STATUS.DIALING,
      current: target,
      // `activeCall` NO se vacía: es la pata de audio de la sesión y sigue viva
      // entre llamadas. Lo que se reinicia es si ESTA llamada está puenteada.
      conectado: false,
      mediaWarning: null,
      screening: false,
    }),

  /**
   * La pata de audio del gestor ya está abierta.
   *
   * Ocurre **una vez por sesión** (y cada ~4 h, cuando Telnyx mata la pata por
   * tiempo), no una vez por llamada. Guardarla aquí no significa que haya
   * conversación: eso lo dice `puenteada`.
   */
  answered: (call) => set({ activeCall: call }),

  /**
   * ESTA llamada ya está puenteada: el deudor y el gestor se oyen.
   *
   * Llega por socket desde el servidor, no del cliente WebRTC. Con la pata
   * aparcada entre llamadas, el SDK solo avisa de la primera: a partir de ahí no
   * hay ningún evento local que distinga "sonando" de "hablando", y la pantalla
   * se quedaría en "Llamando…" con el gestor ya conversando.
   */
  puenteada: () => set({ status: SESSION_STATUS.CONNECTED, conectado: true }),

  /**
   * Terminó la llamada. Lo que estaba en curso pasa a "pendiente de tipificar" y
   * el gestor vuelve a `ready` **sin esperar** a que la cierre: el marcador puede
   * empezar la siguiente mientras él pulsa el resultado de ésta.
   */
  ended: () => {
    const { current, conectado } = get();

    // 🔴 Sin llamada en curso no hay nada que cerrar, y **salir aquí es lo que
    // hace esto idempotente de verdad**.
    //
    // `ended()` lo disparan DOS fuentes: el cliente WebRTC al colgar su pata, y
    // el aviso `call_ended` por socket (la red de seguridad para cuando el
    // puente no llega a construirse). Cuál llega primero no está definido.
    //
    // Sin este corte, la segunda encontraba `current` ya vacío y escribía
    // `pendingOutcome: null`, **borrando la tipificación pendiente que acababa de
    // guardar la primera**. El gestor colgaba y los botones de resultado no
    // llegaban a aparecer: el assignment se quedaba sin cerrar y el deudor
    // volvía a la cola para que le llamaran otra vez.
    //
    // No se notó mientras el puente estaba roto, porque entonces no había pata
    // de audio y solo saltaba una de las dos fuentes.
    if (!current) return undefined;

    return set({
      status: SESSION_STATUS.READY,
      // `activeCall` sobrevive: la pata de audio es de la sesión, y la siguiente
      // llamada va a entrar por ella. Solo se suelta al cerrar sesión.
      conectado: false,
      current: null,
      // `hadAudio` sale de `conectado`, no de `activeCall`. Con la pata
      // permanente, `activeCall` está puesto toda la jornada: derivarlo de ahí
      // contaría cada buzón y cada no-contesta como una conversación.
      pendingOutcome: current ? { ...current, endedAt: new Date(), hadAudio: conectado } : null,
    });
  },

  /**
   * No hay nada que tipificar: era un contestador, o no contestó nadie.
   *
   * 🔴 `ended()` dejaba resultado pendiente SIEMPRE, así que un buzón dejaba el
   * panel "Cierra la llamada con…" abierto pidiéndole al gestor que tipificara
   * una conversación que no existió. Y para quitárselo de en medio tenía que
   * pulsar algo, que `resolveOutcome` suma a las estadísticas: el buzón acababa
   * contando como gestión en sus propios KPIs.
   *
   * Va aparte de `ended()` **a propósito**. El veredicto del AMD llega por
   * socket y el fin de la llamada por el cliente WebRTC, y cuál gana la carrera
   * no está definido; como `ended()` sale pronto si ya no hay llamada en curso,
   * meterlo ahí dentro se perdería cuando llegara el segundo. Así el orden da
   * igual: el aviso limpia lo que haya, llegue antes o después.
   *
   * El `callControlId` se comprueba porque para cuando llega el aviso el gestor
   * puede estar ya en la llamada siguiente, y borrarle esa tipificación dejaría
   * su assignment sin cerrar.
   */
  descartarTipificacion: (callControlId) =>
    set((s) =>
      !s.pendingOutcome || (callControlId && s.pendingOutcome.callAttemptId !== callControlId)
        ? {}
        : { pendingOutcome: null }
    ),

  /**
   * El servidor recuerda una llamada sin clasificar que aquí se había perdido.
   *
   * 🔴 `pendingOutcome` vive en memoria y **este store no se persiste**: una
   * recarga se lleva por delante los botones de tipificar. La pantalla queda
   * impecable y el servidor se queda en `wrap` esperando una tipificación que ya
   * no se puede hacer desde ninguna parte, así que **no vuelve a entrar ninguna
   * llamada**. Visto en producción el 29/09/2026, con el gestor recargando para
   * coger un despliegue.
   *
   * ⚠️ **No pisa lo que ya haya.** El aviso llega en cada barrido, y si el gestor
   * está mirando la tipificación de ESTA llamada, machacarla con la del servidor
   * le cambiaría el panel debajo del ratón. Solo rellena el hueco.
   */
  recuperarTipificacion: (pendiente) =>
    set((s) => {
      if (s.pendingOutcome || !pendiente?.callAttemptId) return {};
      return {
        pendingOutcome: {
          callAttemptId: pendiente.callAttemptId,
          expedientId: pendiente.expedientId || null,
          debtorName: pendiente.debtorName || null,
          endedAt: pendiente.endedAt ? new Date(pendiente.endedAt) : new Date(),
          // No se sabe si hubo audio: esta llamada terminó antes de la recarga y
          // el dato vivía en el store que se perdió. `false` no la cuenta como
          // conversación en los KPIs — preferible a inflarlos con una suposición.
          hadAudio: false,
          recuperada: true,
        },
      };
    }),

  /** El gestor tipificó. Se limpia el pendiente y se suman las estadísticas. */
  resolveOutcome: (outcome, { committedAmount = 0 } = {}) =>
    set((s) => ({
      pendingOutcome: null,
      stats: {
        conversations: s.stats.conversations + (s.pendingOutcome?.hadAudio ? 1 : 0),
        // Los tres códigos: `payment_promise` y `payment_plan` se retiraron el
        // 22/10/2026 y siguen llegando de sesiones abiertas antes del despliegue.
        promises: s.stats.promises + (CALL_OUTCOMES_INTERESTED.includes(outcome) ? 1 : 0),
        committedAmount: s.stats.committedAmount + committedAmount,
      },
    })),

  /**
   * Lo que se le ofrece al gestor justo DESPUÉS de tipificar: ir al expediente,
   * crear la tarea que acordó, o cancelar la llamada que el buzón ha programado.
   *
   * Vive en el store y no en el componente porque la tipificación se puede hacer
   * desde dos sitios —la página y la barra flotante— y las acciones tienen que
   * salir en el que esté mirando.
   */
  ofrecerAcciones: (acciones) => set({ accionesTrasTipificar: acciones || null }),
  cerrarAcciones: () => set({ accionesTrasTipificar: null }),

  /**
   * "Dame un segundo". Pausa el marcador sin cerrar sesión ni cortar el audio.
   *
   * No es una comodidad: la ocupación del gestor pasa de ~30 % a ~65-70 %, y 84
   * conversaciones de recobro seguidas queman. Un marcador sin botón de pausa se
   * acaba desactivando entero.
   */
  pause: () => set({ status: SESSION_STATUS.PAUSED }),
  resume: () => set({ status: SESSION_STATUS.READY }),

  /** Cierra sesión y suelta el cliente. Las estadísticas del día se conservan. */
  reset: () => set({ ...INITIAL, stats: get().stats }),
}));
